import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Space,
  message,
  Popconfirm,
  Tag,
  Card,
  Empty,
} from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import {
  getTransactions,
  getCategories,
  createTransaction,
  updateTransaction,
  deleteTransaction,
} from '../api/budget';

interface IncomeTransaction {
  id: string;
  date: string;
  amount: number;
  category: string;
  note?: string;
  recurrence?: string;
  type: string;
}

interface IncomeCategory {
  id: string;
  name: string;
  type: string;
}

const Income: React.FC = () => {
  const [transactions, setTransactions] = useState<IncomeTransaction[]>([]);
  const [categories, setCategories] = useState<IncomeCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form] = Form.useForm();

  const loadTransactions = async () => {
    setLoading(true);
    try {
      const resp = await getTransactions({ type: 'income' });
      if (resp.ok && resp.data) setTransactions(resp.data as unknown as IncomeTransaction[]);
    } catch (error) {
      message.error('Failed to load income transactions');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const resp = await getCategories();
      const names = resp.data?.income ?? [];
      setCategories(names.map((name, i) => ({ id: String(i), name, type: 'income' as const })));
    } catch (error) {
      console.error('Failed to load categories', error);
    }
  };

  useEffect(() => {
    loadTransactions();
    loadCategories();
  }, []);

  const handleAdd = () => {
    setEditingId(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record: IncomeTransaction) => {
    setEditingId(record.id);
    form.setFieldsValue({
      date: dayjs(record.date),
      amount: record.amount,
      category: record.category,
      note: record.note,
      recurrence: record.recurrence,
    });
    setModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        date: values.date.format('YYYY-MM-DD'),
        amount: values.amount,
        type: 'income',
        category: values.category,
        note: values.note || '',
        recurrence: values.recurrence || 'once',
      };

      if (editingId) {
        await updateTransaction(editingId, payload);
        message.success('Income updated successfully');
      } else {
        await createTransaction(payload);
        message.success('Income added successfully');
      }

      setModalVisible(false);
      form.resetFields();
      loadTransactions();
    } catch (error) {
      message.error(editingId ? 'Failed to update income' : 'Failed to add income');
      console.error(error);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteTransaction(id);
      message.success('Income deleted successfully');
      loadTransactions();
    } catch (error) {
      message.error('Failed to delete income');
      console.error(error);
    }
  };

  const columns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: 120,
      sorter: (a: IncomeTransaction, b: IncomeTransaction) =>
        new Date(a.date).getTime() - new Date(b.date).getTime(),
      render: (date: string) => dayjs(date).format('YYYY-MM-DD'),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 130,
      render: (category: string) => <Tag>{category}</Tag>,
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      align: 'right' as const,
      sorter: (a: IncomeTransaction, b: IncomeTransaction) => a.amount - b.amount,
      render: (amount: number) => (
        <span style={{ color: '#52c41a', fontWeight: 'bold' }}>
          +${amount.toFixed(2)}
        </span>
      ),
    },
    {
      title: 'Note',
      dataIndex: 'note',
      key: 'note',
      render: (note: string) => note || '-',
    },
    {
      title: 'Recurrence',
      dataIndex: 'recurrence',
      key: 'recurrence',
      width: 110,
      render: (recurrence: string) => (
        <Tag color={recurrence === 'once' ? 'default' : 'blue'}>
          {recurrence || 'once'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      fixed: 'right' as const,
      render: (_: unknown, record: IncomeTransaction) => (
        <Space size="small">
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="Delete Income"
            description="Are you sure you want to delete this income record?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="income-page">
      <Card
        title="Income Transactions"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Add Income
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={transactions}
          loading={loading}
          rowKey="id"
          pagination={{ pageSize: 10, showSizeChanger: true }}
          locale={{ emptyText: <Empty description="No income records" /> }}
          scroll={{ x: 800 }}
        />
      </Card>

      <Modal
        title={editingId ? 'Edit Income' : 'Add Income'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        width={500}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Date"
            name="date"
            rules={[{ required: true, message: 'Please select a date' }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            label="Amount"
            name="amount"
            rules={[
              { required: true, message: 'Please enter an amount' },
              {
                pattern: /^\d+(\.\d{1,2})?$/,
                message: 'Please enter a valid amount',
              },
            ]}
          >
            <InputNumber
              min={0}
              step={0.01}
              precision={2}
              style={{ width: '100%' }}
              prefix="$"
            />
          </Form.Item>

          <Form.Item
            label="Category"
            name="category"
            rules={[{ required: true, message: 'Please select a category' }]}
          >
            <Select placeholder="Select a category">
              {categories.map((cat) => (
                <Select.Option key={cat.id} value={cat.name}>
                  {cat.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Note" name="note">
            <Input.TextArea rows={3} placeholder="Add a note (optional)" />
          </Form.Item>

          <Form.Item label="Recurrence" name="recurrence" initialValue="once">
            <Select>
              <Select.Option value="once">Once</Select.Option>
              <Select.Option value="daily">Daily</Select.Option>
              <Select.Option value="weekly">Weekly</Select.Option>
              <Select.Option value="monthly">Monthly</Select.Option>
              <Select.Option value="yearly">Yearly</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Income;
